import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useToast } from './Toast';

const CadrageTiers = () => {
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState('profil');
  const [loading, setLoading] = useState(true);

  // Profil States
  const [cadrage, setCadrage] = useState({
    secteur: 'vtc',
    plateformes_intermediation: true,
    sous_traitance: true,
    regime_tva: 'encaissements',
    reponse_libre: ''
  });
  const [savingProfil, setSavingProfil] = useState(false);
  const [isEditingProfil, setIsEditingProfil] = useState(false);

  // Tiers States
  const [tiers, setTiers] = useState([]);
  const [newTier, setNewTier] = useState({
    id: null,
    nom: '',
    role_pcg: 'client_plateforme',
    compte_pcg_defaut: '411000'
  });
  const [savingTier, setSavingTier] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cadrageRes, tiersRes] = await Promise.all([
        api.get('/copilote/cadrage'),
        api.get('/copilote/tiers')
      ]);
      
      if (cadrageRes && cadrageRes.secteur) {
        setCadrage(cadrageRes);
        // Activer l'édition s'il n'y a pas encore de description personnalisée
        if (cadrageRes.reponse_libre === 'Description de mon activité.' || !cadrageRes.reponse_libre || cadrageRes.secteur === 'autre') {
          setIsEditingProfil(true);
        } else {
          setIsEditingProfil(false);
        }
      }
      if (Array.isArray(tiersRes)) {
        setTiers(tiersRes);
      }
    } catch (err) {
      console.error('Erreur chargement cadrage/tiers:', err);
      addToast('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfil = async (e) => {
    e.preventDefault();
    setSavingProfil(true);
    try {
      const response = await api.post('/copilote/cadrage', cadrage);
      if (response.success) {
        addToast('Profil d\'activité enregistré avec succès !', 'success');
        setIsEditingProfil(false);
      } else {
        addToast('Erreur: ' + response.message, 'error');
      }
    } catch (err) {
      console.error('Erreur sauvegarde profil:', err);
      addToast('Erreur lors de la sauvegarde du profil', 'error');
    } finally {
      setSavingProfil(false);
    }
  };

  const handleSaveTier = async (e) => {
    e.preventDefault();
    if (!newTier.nom.trim()) {
      addToast('Le nom du partenaire est obligatoire', 'warning');
      return;
    }
    setSavingTier(true);
    try {
      const response = await api.post('/copilote/tiers', newTier);
      if (response.success) {
        addToast(isEditing ? 'Partenaire mis à jour !' : 'Partenaire ajouté !', 'success');
        setNewTier({ id: null, nom: '', role_pcg: 'client_plateforme', compte_pcg_defaut: '411000' });
        setIsEditing(false);
        // Recharger le tableau
        const updatedTiers = await api.get('/copilote/tiers');
        if (Array.isArray(updatedTiers)) setTiers(updatedTiers);
      } else {
        addToast('Erreur: ' + response.message, 'error');
      }
    } catch (err) {
      console.error('Erreur enregistrement tiers:', err);
      addToast('Erreur lors de l\'enregistrement', 'error');
    } finally {
      setSavingTier(false);
    }
  };

  const handleEditTier = (tier) => {
    setNewTier(tier);
    setIsEditing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteTier = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce partenaire du référentiel ?')) return;
    try {
      const response = await api.delete(`/copilote/tiers/${id}`);
      if (response.success) {
        addToast('Partenaire supprimé', 'success');
        setTiers(tiers.filter(t => t.id !== id));
      } else {
        addToast('Erreur: ' + response.message, 'error');
      }
    } catch (err) {
      console.error('Erreur suppression tiers:', err);
      addToast('Erreur lors de la suppression', 'error');
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      'client_plateforme': 'Plateforme VTC (Client)',
      'client_prive': 'Client Passager (Privé)',
      'prestataire_sous_traitant': 'Chauffeur Sous-traitant',
      'fournisseur_exploitation': 'Fournisseur d\'Exploitation'
    };
    return labels[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      'client_plateforme': { bg: '#E1F5EE', text: '#0F6E56' },
      'client_prive': { bg: '#EFF6FF', text: '#1E40AF' },
      'prestataire_sous_traitant': { bg: '#FAF5FF', text: '#6B21A8' },
      'fournisseur_exploitation': { bg: '#FFF7ED', text: '#9A3412' }
    };
    return colors[role] || { bg: '#F3F4F6', text: '#374151' };
  };

  if (loading) {
    return (
      <div style={{background:'white', padding:'24px', borderRadius:'12px', border:'1px solid #E8E7E3', textAlign:'center'}}>
        Chargement de l'assistant de cadrage...
      </div>
    );
  }

  return (
    <div style={{background:'white', padding:'24px', borderRadius:'12px', border:'1px solid #E8E7E3', fontFamily:'Inter, sans-serif'}}>
      <h2 style={{fontSize:'20px', fontWeight:'600', marginBottom:'6px', color:'#1E293B'}}>🎯 Cadrage Métier & Référentiel Tiers</h2>
      <p style={{color:'#64748B', fontSize:'13px', marginBottom:'20px'}}>
        Configurez le fonctionnement de votre entreprise et l'annuaire de vos partenaires pour garantir des écritures comptables sans erreurs.
      </p>

      {/* Tabs */}
      <div style={{display:'flex', borderBottom:'1px solid #E2E8F0', marginBottom:'24px', gap:'16px'}}>
        <button
          onClick={() => setActiveTab('profil')}
          style={{
            padding:'10px 4px',
            border:'none',
            background:'none',
            fontSize:'14px',
            fontWeight: activeTab === 'profil' ? '600' : '400',
            color: activeTab === 'profil' ? '#0F6E56' : '#64748B',
            borderBottom: activeTab === 'profil' ? '2px solid #0F6E56' : 'none',
            cursor:'pointer'
          }}
        >
          🎯 Profil d'Activité
        </button>
        <button
          onClick={() => setActiveTab('tiers')}
          style={{
            padding:'10px 4px',
            border:'none',
            background:'none',
            fontSize:'14px',
            fontWeight: activeTab === 'tiers' ? '600' : '400',
            color: activeTab === 'tiers' ? '#0F6E56' : '#64748B',
            borderBottom: activeTab === 'tiers' ? '2px solid #0F6E56' : 'none',
            cursor:'pointer'
          }}
        >
          👥 Annuaire des Partenaires
        </button>
      </div>

      {activeTab === 'profil' ? (
        !isEditingProfil ? (
          // VUE LECTURE - TABLEAU DE BORD DU CADRAGE
          <div style={{display:'flex', flexDirection:'column', gap:'20px', maxWidth:'650px'}}>
            
            {/* Bannière de statut de l'IA */}
            <div style={{background:'#E1F5EE', border:'1px solid #A7F3D0', padding:'16px', borderRadius:'10px', display:'flex', alignItems:'center', gap:'12px'}}>
              <span style={{fontSize:'20px'}}>🛡️</span>
              <div>
                <h4 style={{fontSize:'14px', fontWeight:'600', color:'#0F6E56', margin:0}}>Profil d'activité configuré et actif</h4>
                <p style={{fontSize:'12px', color:'#1D9E75', margin:'2px 0 0'}}>Les moteurs d'extraction OCR et de classification IA utilisent ces paramètres pour automatiser vos écritures.</p>
              </div>
            </div>

            {/* Grille de synthèse */}
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:'16px'}}>
              
              <div style={{border:'1px solid #F1F5F9', background:'#F8FAFC', padding:'16px', borderRadius:'8px'}}>
                <span style={{fontSize:'12px', color:'#64748B', fontWeight:'500'}}>Secteur d'activité</span>
                <div style={{fontSize:'14px', fontWeight:'600', color:'#1E293B', marginTop:'6px', display:'flex', alignItems:'center', gap:'6px'}}>
                  🚕 {cadrage.secteur === 'vtc' ? 'Transport VTC / Flotte de Chauffeurs' :
                       cadrage.secteur === 'ecommerce' ? 'E-commerce / Boutique en ligne' :
                       cadrage.secteur === 'freelance' ? 'Conseil / Prestations intellectuelles' :
                       cadrage.secteur === 'btp' ? 'Bâtiment / Artisan / Travaux' : 'Autre secteur d\'activité'}
                </div>
              </div>

              <div style={{border:'1px solid #F1F5F9', background:'#F8FAFC', padding:'16px', borderRadius:'8px'}}>
                <span style={{fontSize:'12px', color:'#64748B', fontWeight:'500'}}>Régime de TVA</span>
                <div style={{fontSize:'14px', fontWeight:'600', color:'#1E293B', marginTop:'6px', display:'flex', alignItems:'center', gap:'6px'}}>
                  📝 {cadrage.regime_tva === 'encaissements' ? 'TVA sur les Encaissements' :
                       cadrage.regime_tva === 'debits' ? 'TVA sur les Débits' : 'Franchise en Base (Exonéré)'}
                </div>
              </div>

              <div style={{border:'1px solid #F1F5F9', background:'#F8FAFC', padding:'16px', borderRadius:'8px', gridColumn:'1 / -1'}}>
                <span style={{fontSize:'12px', color:'#64748B', fontWeight:'500'}}>Options opérationnelles</span>
                <div style={{display:'flex', gap:'12px', marginTop:'8px', flexWrap:'wrap'}}>
                  <span style={{
                    background: cadrage.plateformes_intermediation ? '#E1F5EE' : '#F1F5F9',
                    color: cadrage.plateformes_intermediation ? '#0F6E56' : '#64748B',
                    fontSize:'12px', fontWeight:'500', padding:'4px 10px', borderRadius:'12px'
                  }}>
                    {cadrage.plateformes_intermediation ? '✓ Plateformes d\'intermédiation actives' : '✗ Pas de plateformes'}
                  </span>
                  <span style={{
                    background: cadrage.sous_traitance ? '#E1F5EE' : '#F1F5F9',
                    color: cadrage.sous_traitance ? '#0F6E56' : '#64748B',
                    fontSize:'12px', fontWeight:'500', padding:'4px 10px', borderRadius:'12px'
                  }}>
                    {cadrage.sous_traitance ? '✓ Sous-traitance opérationnelle active' : '✗ Pas de sous-traitance'}
                  </span>
                </div>
              </div>

            </div>

            {/* Description métier */}
            <div style={{border:'1px solid #E2E8F0', padding:'16px', borderRadius:'8px', background:'white'}}>
              <span style={{fontSize:'12px', color:'#64748B', fontWeight:'500', display:'block', marginBottom:'8px'}}>
                Description métier (interprétée par l'IA) :
              </span>
              <blockquote style={{
                margin:0,
                paddingLeft:'16px',
                borderLeft:'4px solid #0F6E56',
                fontStyle:'italic',
                color:'#334155',
                fontSize:'13px',
                lineHeight:'1.6'
              }}>
                "{cadrage.reponse_libre}"
              </blockquote>
            </div>

            <button
              onClick={() => setIsEditingProfil(true)}
              style={{
                background:'white',
                color:'#475569',
                border:'1px solid #CBD5E1',
                padding:'8px 14px',
                borderRadius:'6px',
                fontSize:'13px',
                fontWeight:'500',
                cursor:'pointer',
                alignSelf:'flex-start',
                transition:'background 0.15s, color 0.15s'
              }}
              onMouseEnter={e => { e.target.style.background = '#F8FAFC'; e.target.style.color = '#1E293B'; }}
              onMouseLeave={e => { e.target.style.background = 'white'; e.target.style.color = '#475569'; }}
            >
              ✏️ Éditer les informations
            </button>

          </div>
        ) : (
          // VUE ÉDITION - FORMULAIRE DE MODIFICATION
          <form onSubmit={handleSaveProfil} style={{maxWidth:'650px'}}>
            <div style={{display:'flex', flexDirection:'column', gap:'18px'}}>
              
              <div>
                <label style={{display:'block', fontSize:'13px', fontWeight:'500', color:'#334155', marginBottom:'6px'}}>
                  Quel est votre secteur d'activité ?
                </label>
                <select
                  value={cadrage.secteur}
                  onChange={e => setCadrage({...cadrage, secteur: e.target.value})}
                  style={{width:'100%', padding:'8px 12px', borderRadius:'6px', border:'1px solid #CBD5E1', fontSize:'13px'}}
                >
                  <option value="vtc">Transport VTC / Flotte de Chauffeurs</option>
                  <option value="ecommerce">E-commerce / Boutique en ligne</option>
                  <option value="freelance">Prestation de services intellectuels (Freelance / Conseil)</option>
                  <option value="btp">Bâtiment / Artisan / Travaux</option>
                  <option value="autre">Autre secteur d'activité</option>
                </select>
              </div>

              <div style={{display:'flex', gap:'24px', background:'#FAF9F5', padding:'12px', borderRadius:'8px'}}>
                <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                  <input
                    type="checkbox"
                    id="plat"
                    checked={cadrage.plateformes_intermediation}
                    onChange={e => setCadrage({...cadrage, plateformes_intermediation: e.target.checked})}
                    style={{cursor:'pointer'}}
                  />
                  <label htmlFor="plat" style={{fontSize:'13px', cursor:'pointer', color:'#334155'}}>
                    Utilisation de plateformes d'intermédiation / apporteurs d'affaires
                  </label>
                </div>

                <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                  <input
                    type="checkbox"
                    id="sous"
                    checked={cadrage.sous_traitance}
                    onChange={e => setCadrage({...cadrage, sous_traitance: e.target.checked})}
                    style={{cursor:'pointer'}}
                  />
                  <label htmlFor="sous" style={{fontSize:'13px', cursor:'pointer', color:'#334155'}}>
                    Faire appel à des sous-traitants
                  </label>
                </div>
              </div>

              <div>
                <label style={{display:'block', fontSize:'13px', fontWeight:'500', color:'#334155', marginBottom:'6px'}}>
                  Comment payez-vous la TVA sur votre chiffre d'affaires ?
                </label>
                <select
                  value={cadrage.regime_tva}
                  onChange={e => setCadrage({...cadrage, regime_tva: e.target.value})}
                  style={{width:'100%', padding:'8px 12px', borderRadius:'6px', border:'1px solid #CBD5E1', fontSize:'13px'}}
                >
                  <option value="encaissements">TVA sur les Encaissements (Déclarée lors du paiement effectif)</option>
                  <option value="debits">TVA sur les Débits (Déclarée dès la facturation)</option>
                  <option value="franchise">Franchise en Base (Exonéré de TVA / Micro-entreprise)</option>
                </select>
              </div>

              <div>
                <label style={{display:'block', fontSize:'13px', fontWeight:'500', color:'#334155', marginBottom:'6px'}}>
                  Décrivez avec vos propres mots le fonctionnement de votre activité (Réponse libre) :
                </label>
                <textarea
                  value={cadrage.reponse_libre || ''}
                  onChange={e => setCadrage({...cadrage, reponse_libre: e.target.value})}
                  placeholder="Exemple: Je vends des prestations de services. Mes clients me paient par virement bancaire. Pour certaines missions, je fais appel à des prestataires sous-traitants..."
                  style={{width:'100%', height:'120px', padding:'8px 12px', borderRadius:'6px', border:'1px solid #CBD5E1', fontSize:'13px', fontFamily:'inherit'}}
                />
                <span style={{fontSize:'11px', color:'#64748B'}}>
                  💡 Notre IA s'appuie sur cette description pour comprendre vos spécificités et adapter vos imports de documents.
                </span>
              </div>

              <div style={{display:'flex', gap:'8px'}}>
                <button
                  type="submit"
                  disabled={savingProfil}
                  style={{
                    background:'#0F6E56',
                    color:'white',
                    border:'none',
                    padding:'10px 16px',
                    borderRadius:'6px',
                    fontSize:'13px',
                    fontWeight:'500',
                    cursor:'pointer',
                    transition:'background 0.15s'
                  }}
                  onMouseEnter={e => e.target.style.background = '#0d5c48'}
                  onMouseLeave={e => e.target.style.background = '#0F6E56'}
                >
                  {savingProfil ? 'Enregistrement...' : '✓ Enregistrer le Profil'}
                </button>
                {cadrage.reponse_libre && cadrage.reponse_libre !== 'Description de mon activité.' && (
                  <button
                    type="button"
                    onClick={() => setIsEditingProfil(false)}
                    style={{
                      background:'white',
                      color:'#475569',
                      border:'1px solid #CBD5E1',
                      padding:'10px 16px',
                      borderRadius:'6px',
                      fontSize:'13px',
                      fontWeight:'500',
                      cursor:'pointer',
                      transition:'background 0.15s'
                    }}
                    onMouseEnter={e => e.target.style.background = '#F8FAFC'}
                    onMouseLeave={e => e.target.style.background = 'white'}
                  >
                    Annuler
                  </button>
                )}
              </div>

            </div>
          </form>
        )
      ) : (
        <div>
          {/* Form tiers */}
          <div style={{background:'#F8FAFC', border:'1px solid #E2E8F0', padding:'16px', borderRadius:'8px', marginBottom:'24px'}}>
            <h3 style={{fontSize:'14px', fontWeight:'600', marginBottom:'12px', color:'#1E293B'}}>
              {isEditing ? '⚙ Modifier le Partenaire' : '➕ Ajouter un Partenaire au Référentiel'}
            </h3>
            <form onSubmit={handleSaveTier} style={{display:'flex', flexWrap:'wrap', gap:'12px', alignItems:'flex-end'}}>
              <div style={{flex:'1 1 200px'}}>
                <label style={{display:'block', fontSize:'11px', fontWeight:'500', color:'#475569', marginBottom:'4px'}}>
                  Nom du tiers (tel qu'il apparaît sur les factures)
                </label>
                <input
                  type="text"
                  placeholder="Ex: Nom du partenaire commercial, Nom du fournisseur..."
                  value={newTier.nom}
                  onChange={e => setNewTier({...newTier, nom: e.target.value})}
                  style={{width:'100%', padding:'7px 10px', borderRadius:'4px', border:'1px solid #CBD5E1', fontSize:'13px'}}
                />
              </div>

              <div style={{flex:'1 1 180px'}}>
                <label style={{display:'block', fontSize:'11px', fontWeight:'500', color:'#475569', marginBottom:'4px'}}>
                  Rôle de ce partenaire
                </label>
                <select
                  value={newTier.role_pcg}
                  onChange={e => {
                    const role = e.target.value;
                    const defAcc = role.startsWith('client') ? '411000' : '401000';
                    setNewTier({...newTier, role_pcg: role, compte_pcg_defaut: defAcc});
                  }}
                  style={{width:'100%', padding:'7px 10px', borderRadius:'4px', border:'1px solid #CBD5E1', fontSize:'13px', background:'white'}}
                >
                  <option value="client_plateforme">Plateforme VTC (Client)</option>
                  <option value="client_prive">Client Passager (Privé)</option>
                  <option value="prestataire_sous_traitant">Chauffeur Sous-traitant</option>
                  <option value="fournisseur_exploitation">Fournisseur d'Exploitation</option>
                </select>
              </div>

              <div style={{flex:'1 1 100px'}}>
                <label style={{display:'block', fontSize:'11px', fontWeight:'500', color:'#475569', marginBottom:'4px'}}>
                  Compte PCG
                </label>
                <input
                  type="text"
                  value={newTier.compte_pcg_defaut}
                  onChange={e => setNewTier({...newTier, compte_pcg_defaut: e.target.value})}
                  style={{width:'100%', padding:'7px 10px', borderRadius:'4px', border:'1px solid #CBD5E1', fontSize:'13px'}}
                />
              </div>

              <div style={{display:'flex', gap:'8px'}}>
                <button
                  type="submit"
                  disabled={savingTier}
                  style={{
                    background:'#0F6E56',
                    color:'white',
                    border:'none',
                    padding:'8px 14px',
                    borderRadius:'4px',
                    fontSize:'13px',
                    fontWeight:'500',
                    cursor:'pointer'
                  }}
                >
                  {savingTier ? 'Enregistrement...' : isEditing ? 'Mettre à jour' : 'Ajouter'}
                </button>
                {isEditing && (
                  <button
                    type="button"
                    onClick={() => {
                      setNewTier({ id: null, nom: '', role_pcg: 'client_plateforme', compte_pcg_defaut: '411000' });
                      setIsEditing(false);
                    }}
                    style={{
                      background:'#E2E8F0',
                      color:'#475569',
                      border:'none',
                      padding:'8px 14px',
                      borderRadius:'4px',
                      fontSize:'13px',
                      cursor:'pointer'
                    }}
                  >
                    Annuler
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Table */}
          <div style={{overflowX:'auto', border:'1px solid #E2E8F0', borderRadius:'6px'}}>
            <table style={{width:'100%', borderCollapse:'collapse', fontSize:'13px', textAlign:'left'}}>
              <thead>
                <tr style={{background:'#F8FAFC', borderBottom:'1px solid #E2E8F0'}}>
                  <th style={{padding:'10px 12px', fontWeight:'600', color:'#475569'}}>Nom du Partenaire</th>
                  <th style={{padding:'10px 12px', fontWeight:'600', color:'#475569'}}>Rôle Comptable</th>
                  <th style={{padding:'10px 12px', fontWeight:'600', color:'#475569'}}>Compte par Défaut</th>
                  <th style={{padding:'10px 12px', fontWeight:'600', color:'#475569', textAlign:'right'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tiers.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{padding:'20px', textAlign:'center', color:'#64748B'}}>
                      Aucun partenaire enregistré pour le moment.
                    </td>
                  </tr>
                ) : (
                  tiers.map(t => {
                    const color = getRoleColor(t.role_pcg);
                    return (
                      <tr key={t.id} style={{borderBottom:'1px solid #F1F5F9'}}>
                        <td style={{padding:'10px 12px', fontWeight:'500', color:'#1E293B'}}>{t.nom}</td>
                        <td style={{padding:'10px 12px'}}>
                          <span style={{
                            background: color.bg,
                            color: color.text,
                            fontSize: '11px',
                            fontWeight: '500',
                            padding: '2px 8px',
                            borderRadius: '12px'
                          }}>
                            {getRoleLabel(t.role_pcg)}
                          </span>
                        </td>
                        <td style={{padding:'10px 12px', fontFamily:'monospace', color:'#475569'}}>{t.compte_pcg_defaut}</td>
                        <td style={{padding:'10px 12px', textAlign:'right'}}>
                          <button
                            onClick={() => handleEditTier(t)}
                            style={{
                              background:'none',
                              border:'none',
                              color:'#0F6E56',
                              fontSize:'12px',
                              cursor:'pointer',
                              marginRight:'12px',
                              fontWeight:'500'
                            }}
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() => handleDeleteTier(t.id)}
                            style={{
                              background:'none',
                              border:'none',
                              color:'#EF4444',
                              fontSize:'12px',
                              cursor:'pointer',
                              fontWeight:'500'
                            }}
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

        </div>
      )}

    </div>
  );
};

export default CadrageTiers;
